import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Store, MapPin, Phone, Mail,
  ExternalLink, Loader2, Gift, CheckCircle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { venueApi } from '../../lib/api';
import { resolveAssetUrl } from '../../config';
import { toast } from 'sonner';
import VenueHeader from '../../components/venue/VenueHeader';

const VenueAdvertisers = () => {
  const { user, loading: authLoading } = useAuth();
  const [venuePartner, setVenuePartner] = useState(null);
  const [location, setLocation] = useState(null);
  const [advertisers, setAdvertisers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Wait for AuthContext to resolve before touching user.venue_partner_id.
    // The old one-shot [] effect dereferenced it on first render and crashed
    // the page on any direct load or refresh.
    if (authLoading) return;
    // Access is enforced by <RequireVenue> in App.js; a partner whose profile
    // carries no hub yet simply has nothing to fetch.
    if (!user?.venue_partner_id) {
      setLoading(false);
      return;
    }
    fetchData();
  }, [user, authLoading]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const venueResponse = await venueApi.get(
        `/venue/partner/${user.venue_partner_id}`
      );
      setVenuePartner(venueResponse.data);

      if (venueResponse.data.hub_location_id) {
        const locationResponse = await venueApi.get(
          `/locations/${venueResponse.data.hub_location_id}`
        );
        setLocation(locationResponse.data);

        const businessesResponse = await venueApi.get(
          `/locations/${venueResponse.data.hub_location_id}/businesses`
        );
        setAdvertisers(businessesResponse.data);
      }
    } catch (error) {
      console.error('Failed to fetch advertisers:', error);
      toast.error('Failed to load advertisers');
    } finally {
      setLoading(false);
    }
  };

  // V5 Loading State
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FEF7ED' }}>
        <div className="text-center">
          <img src="/tappd-logo.png" alt="Tappd" className="h-12 mx-auto mb-6" />
          <Loader2 className="animate-spin mx-auto mb-4" size={48} style={{ color: '#14b8a6' }} />
          <p className="text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>Loading advertisers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FEF7ED' }}>
      {/* V5 Venue Header with Mobile Nav */}
      <VenueHeader user={user} venuePartner={venuePartner} locationData={location} />

      {/* Content */}
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 pt-24">
        {/* V5 Page Header */}
        <div className="mb-8">
          <p className="font-mono text-xs mb-2" style={{ color: '#14b8a6' }}>
            // venue_advertisers
          </p>
          <h1 
            className="text-[32px] mb-1"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, color: '#1e1b4b' }}
          >
            Advertisers at Your Location
          </h1>
          <p className="text-[14px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
            Businesses promoting deals through your venue
          </p>
        </div>

        {/* V5 Stats Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-6 mb-8"
          style={{ 
            borderRadius: '12px',
            border: '1px solid rgba(30, 27, 75, 0.05)',
            borderLeft: '4px solid #f97316',
            boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
          }}
        >
          <div className="flex items-center gap-6">
            <div 
              className="w-16 h-16 flex items-center justify-center"
              style={{ backgroundColor: 'rgba(249, 115, 22, 0.1)', borderRadius: '12px' }}
            >
              <Users size={32} style={{ color: '#f97316' }} />
            </div>
            <div>
              <p className="text-[13px] mb-1" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>Total Active Advertisers</p>
              <p className="text-[48px] font-bold" style={{ color: '#f97316' }}>
                {advertisers.length}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Advertisers List */}
        {advertisers.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white p-12 text-center"
            style={{ 
              borderRadius: '12px',
              border: '1px solid rgba(30, 27, 75, 0.05)',
              borderLeft: '4px solid #14b8a6',
              boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
            }}
          >
            <div 
              className="w-20 h-20 mx-auto mb-6 flex items-center justify-center"
              style={{ backgroundColor: 'rgba(30, 27, 75, 0.05)', borderRadius: '12px' }}
            >
              <Store size={40} style={{ color: 'rgba(30, 27, 75, 0.3)' }} />
            </div>
            <h3 
              className="text-[24px] mb-4"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500, color: '#1e1b4b' }}
            >
              No Advertisers Yet
            </h3>
            <p className="text-[14px] mb-6" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
              Businesses haven&apos;t started advertising at your location yet. 
              Once they do, you&apos;ll see them here!
            </p>
            <div 
              className="max-w-2xl mx-auto p-6"
              style={{ 
                backgroundColor: 'rgba(20, 184, 166, 0.05)',
                border: '1px solid rgba(20, 184, 166, 0.1)',
                borderRadius: '12px'
              }}
            >
              <h4 className="font-semibold text-[14px] mb-3" style={{ color: '#14b8a6' }}>How it works:</h4>
              <ul className="text-left space-y-3">
                {[
                  'Local businesses choose your venue as their hub location',
                  'Their deals become visible when customers scan your coasters',
                  'You connect your customers with nearby businesses'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                    <CheckCircle size={18} className="flex-shrink-0 mt-0.5" style={{ color: '#22c55e' }} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {advertisers.map((business, index) => (
              <motion.div
                key={business.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white h-full"
                style={{ 
                  borderRadius: '12px',
                  border: '1px solid rgba(30, 27, 75, 0.05)',
                  borderLeft: '4px solid #8b5cf6',
                  boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
                }}
              >
                <div className="p-6">
                  {/* Business Banner/Logo Image */}
                  {(business.image_url || business.logo_url) ? (
                    <div
                      className="w-full h-32 mb-4 overflow-hidden"
                      style={{ borderRadius: '12px' }}
                    >
                      <img
                        src={resolveAssetUrl(business.image_url || business.logo_url)}
                        alt={business.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `<div class="w-full h-full flex items-center justify-center" style="background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(249, 115, 22, 0.1))"><svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7"/><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4"/><path d="M2 7h20"/><path d="M22 7v3a2 2 0 0 1-2 2v0a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 16 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 12 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 8 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 4 12v0a2 2 0 0 1-2-2V7"/></svg></div>`;
                        }}
                      />
                    </div>
                  ) : (
                    <div 
                      className="w-full h-32 mb-4 flex items-center justify-center"
                      style={{ 
                        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(249, 115, 22, 0.1))',
                        borderRadius: '12px'
                      }}
                    >
                      <Store size={48} style={{ color: '#8b5cf6' }} />
                    </div>
                  )}

                  {/* Business Info */}
                  <h3 
                    className="text-[18px] font-semibold mb-2"
                    style={{ fontFamily: "'DM Sans', sans-serif", color: '#1e1b4b' }}
                  >
                    {business.name}
                  </h3>
                  
                  {business.category && (
                    <p className="text-[12px] uppercase tracking-wide mb-3" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                      {business.category}
                    </p>
                  )}

                  <div className="space-y-2 mb-4">
                    {business.address && (
                      <div className="flex items-start gap-2 text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                        <MapPin size={16} className="flex-shrink-0 mt-0.5" style={{ color: '#14b8a6' }} />
                        <span>{business.address}</span>
                      </div>
                    )}
                    
                    {business.phone && (
                      <div className="flex items-center gap-2 text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                        <Phone size={16} style={{ color: '#14b8a6' }} />
                        <span>{business.phone}</span>
                      </div>
                    )}
                    
                    {business.email && (
                      <div className="flex items-center gap-2 text-[13px]" style={{ color: 'rgba(30, 27, 75, 0.7)' }}>
                        <Mail size={16} style={{ color: '#14b8a6' }} />
                        <span className="truncate">{business.email}</span>
                      </div>
                    )}
                  </div>

                  {/* Active Deals Count */}
                  {business.active_deals_count !== undefined && (
                    <div 
                      className="p-3 mb-4"
                      style={{ 
                        backgroundColor: 'rgba(139, 92, 246, 0.05)',
                        border: '1px solid rgba(139, 92, 246, 0.1)',
                        borderRadius: '12px'
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <Gift size={20} style={{ color: '#8b5cf6' }} />
                        <div>
                          <p className="text-[13px] font-semibold" style={{ color: '#1e1b4b' }}>
                            {business.active_deals_count} Active Deal{business.active_deals_count !== 1 ? 's' : ''}
                          </p>
                          <p className="text-[12px]" style={{ color: 'rgba(30, 27, 75, 0.5)' }}>
                            Visible to customers who scan
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* View Business Button */}
                  {business.slug && (
                    <button
                      onClick={() => window.open(`/business/${business.slug}`, '_blank')}
                      className="w-full px-4 py-3 flex items-center justify-center gap-2 text-[14px] font-semibold transition-all"
                      style={{
                        backgroundColor: 'white',
                        border: '2px solid rgba(139, 92, 246, 0.3)',
                        borderRadius: '12px',
                        color: '#8b5cf6'
                      }}
                    >
                      <ExternalLink size={16} />
                      View Business Page
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VenueAdvertisers;
